import { useEffect, useState, type ReactElement } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type {
  RoleAssignment,
  RoleAssignmentWrite,
  RoleAssignmentsResponse,
} from '@citibox-panel/api-client';
import { useAuthContext } from '@citibox-panel/auth';
import { Box, SurfaceHeader } from '@citibox-panel/ui';
import { UserMenu } from '@/features/user-menu';
import { AccessStateScreen } from '@/components/AccessStateScreen';
import type { ViewHome } from '@/components/RequireView';
import { can } from '@/lib/access';
import {
  fetchRoleAssignments as fetchRoleAssignmentsFromApi,
  removeAllRoleAssignment as removeAllRoleAssignmentFromApi,
  upsertRoleAssignment as upsertRoleAssignmentFromApi,
} from '@/lib/apiClient';
import { MARKET_LABELS, resolveMarket, resolveMarketIndicator } from '@/lib/marketIndicator';
import { readRuntimeConfig } from '@/lib/runtimeConfig';
import { useSession } from '@/lib/session';
import { signOutOfPanel } from '@/lib/signOut';
import { ROLE_ASSIGNMENTS_KEY, useRoleAssignments } from '../hooks/useRoleAssignments';
import {
  ROLE_ASSIGNMENTS_WRITE_KEY,
  useSaveRoleAssignment,
  type SaveRequest,
} from '../hooks/useSaveRoleAssignment';
import { canonicalEmail } from '../lib/email';
import { isAccessManageDenied } from '../lib/errors';
import { RolesManagementPage, type RolesManagementPageProps } from './RolesManagementPage';

export interface RolesManagementRouteProps {
  // Injected by tests; production binds the `@/lib/apiClient` functions, which own the mock switch.
  fetchRoleAssignments?: () => Promise<RoleAssignmentsResponse>;
  upsertRoleAssignment?: (write: RoleAssignmentWrite) => Promise<void>;
  removeAllRoleAssignment?: (email: string, expectedVersion: string) => Promise<void>;
  // Where «Ir al inicio» leads when the server denies this screen mid-session. The route table owns
  // every path, so it hands the same home every RequireView uses rather than the slice naming one.
  home: ViewHome;
}

// Composition root of «Roles y permisos»: the surface band with the user menu, the market labels,
// the read and the write, and the denied state the server can force on it mid-session.
export function RolesManagementRoute({
  fetchRoleAssignments = fetchRoleAssignmentsFromApi,
  upsertRoleAssignment = upsertRoleAssignmentFromApi,
  removeAllRoleAssignment = removeAllRoleAssignmentFromApi,
  home,
}: RolesManagementRouteProps): ReactElement {
  const { permissions, roleNames } = useSession();
  const { firebaseUser } = useAuthContext();
  const queryClient = useQueryClient();
  // Once the server refuses access management, this mount never reads the list again: the denied
  // view must not turn into a loop of 403s.
  const [denied, setDenied] = useState(false);
  const query = useRoleAssignments(fetchRoleAssignments, !denied);
  const mutation = useSaveRoleAssignment({
    upsert: upsertRoleAssignment,
    removeAll: removeAllRoleAssignment,
  });

  // TanStack Query clears `error` and puts a query without data back to `pending` while it
  // refetches, so the last read failure is kept here: a «Reintentar» in flight stays on the failure,
  // busy, instead of falling back to «Cargando…» and dropping the keyboard on <body>.
  const [lastError, setLastError] = useState<unknown>(null);
  if (query.error !== null && query.error !== lastError) setLastError(query.error);

  const uid = firebaseUser?.uid ?? null;
  const email = firebaseUser?.email ?? null;
  const refusedByServer = isAccessManageDenied(query.error) || isAccessManageDenied(mutation.error);

  // Runs once per refusal: after it, `denied` holds and the guard returns. The cached list goes
  // (nobody should see it again without the permission), so does a refused write with the email and
  // roles it carried, and /me is re-read once so the session catches up with what the server knows.
  useEffect(() => {
    if (!refusedByServer || denied) return;
    setDenied(true);
    queryClient.removeQueries({ queryKey: ROLE_ASSIGNMENTS_KEY });
    const mutations = queryClient.getMutationCache();
    mutations.findAll({ mutationKey: ROLE_ASSIGNMENTS_WRITE_KEY }).forEach((write) => {
      mutations.remove(write);
    });
    if (uid !== null) void queryClient.invalidateQueries({ queryKey: ['me', uid] });
  }, [refusedByServer, denied, queryClient, uid]);

  if (denied || refusedByServer) {
    return (
      <AccessStateScreen
        variant="denied"
        email={email}
        onSignOut={signOutOfPanel}
        {...(can(permissions, home.permission) ? { homeHref: home.to } : {})}
      />
    );
  }

  const config = readRuntimeConfig();
  const indicator = resolveMarketIndicator(config);
  const market = resolveMarket(config);
  // The bare market for the copy («ES»), not the pill's text: null when it cannot be confirmed.
  const marketLabel = market === null ? null : MARKET_LABELS[market];

  // Data on screen wins over a failed refetch: the list stays readable while «Reintentar» runs.
  // Without data, the kept failure holds the error state through the retry.
  const readFailure = query.error ?? lastError;
  const status: RolesManagementPageProps['status'] =
    query.data !== undefined ? 'ready' : readFailure !== null ? 'error' : 'loading';

  const save = async (request: SaveRequest): Promise<void> => {
    await mutation.mutateAsync(request);
  };

  // «Cargar la versión actual»: a fresh read of the whole list (the resource has no per-person
  // read), which also refreshes the table behind the dialog. It must not inherit the client's
  // default retry (a 403 is never asked twice) nor pause offline (the dialog would stay locked).
  const reloadCurrent = async (target: string): Promise<RoleAssignment | null> => {
    const fresh = await queryClient.fetchQuery({
      queryKey: ROLE_ASSIGNMENTS_KEY,
      queryFn: fetchRoleAssignments,
      staleTime: 0,
      retry: false,
      networkMode: 'always',
    });
    const wanted = canonicalEmail(target);
    return fresh.assignments.find((assignment) => assignment.email === wanted) ?? null;
  };

  return (
    <>
      <SurfaceHeader
        brandLabel={indicator.text}
        brandDegraded={indicator.degraded}
        end={<UserMenu roleNames={roleNames} />}
      />
      {/* The content sits on paper, not on the page's gray: the muted subtitle and empty states
          clear AA on white but drop below it on the gray. */}
      <Box
        component="section"
        sx={{ p: 3, width: '100%', maxWidth: 1200, mx: 'auto', bgcolor: 'background.paper' }}
      >
        <RolesManagementPage
          data={query.data}
          status={status}
          onRetry={() => {
            void query.refetch();
          }}
          retrying={query.isFetching}
          selfEmail={email}
          marketLabel={marketLabel}
          save={save}
          pending={mutation.isPending}
          onReloadCurrent={reloadCurrent}
        />
      </Box>
    </>
  );
}
